import { useEffect, useState } from 'react';
import { api, errorText, formatDate, formatMoney, formatQty } from '../api';
import { Card, Empty, Loading, Notice } from '../components/ui';

interface ManagementReport {
  revenue: { month: string; booked_cents: number; draft_cents: number }[];
  revenueByOwner: { owner_id: string; owner_name: string; booked_cents: number; invoices: number }[];
  unbilled: { owner_name: string; events: number; amount_cents: number }[];
  throughput: { week: string; eingang: number; ausgang: number }[];
  pickPerformance: { user_name: string; picks: number; units: number }[];
  utilisation: {
    locations_total: number;
    locations_used: number;
    stock_units: number;
    stock_value_cents: number;
  };
  slowMovers: {
    sku: string;
    article_name: string;
    owner_name: string;
    qty: number;
    last_movement: string | null;
  }[];
  negativeStock: {
    sku: string;
    article_name: string;
    owner_name: string;
    location_code: string;
    qty: number;
    last_movement: string | null;
  }[];
  serviceLevel: { shipped: number; onTime: number; withoutDate: number; percent: number | null };
  openWork: {
    inbound: number;
    outbound: number;
    pickTasks: number;
    returns: number;
    counts: number;
    failedJobs: number;
  };
}

function Bars({
  data,
  labelOf,
  valuesOf,
  colors,
}: {
  data: unknown[];
  labelOf: (row: never) => string;
  valuesOf: (row: never) => number[];
  colors: string[];
}) {
  const rows = data as never[];
  const max = Math.max(1, ...rows.flatMap((row) => valuesOf(row)));

  return (
    <div>
      <div className="spark" style={{ height: 120, alignItems: 'flex-end' }}>
        {rows.map((row, index) => (
          <div key={index} className="day" style={{ justifyContent: 'flex-end' }} title={labelOf(row)}>
            {valuesOf(row).map((value, i) => (
              <div
                key={i}
                style={{
                  background: colors[i],
                  height: `${(value / max) * 100}%`,
                  minHeight: value > 0 ? 2 : 0,
                  borderRadius: '2px 2px 0 0',
                }}
              />
            ))}
          </div>
        ))}
      </div>
      <div
        style={{
          display: 'flex',
          gap: 3,
          marginTop: 6,
          fontSize: 10,
          color: 'var(--ink-soft)',
        }}
      >
        {rows.map((row, index) => (
          <div key={index} style={{ flex: 1, textAlign: 'center' }}>
            {labelOf(row)}
          </div>
        ))}
      </div>
    </div>
  );
}

function Tile({ label, value, hint, alert }: { label: string; value: string; hint?: string; alert?: boolean }) {
  return (
    <div className={alert ? 'kpi-tile alert' : 'kpi-tile'}>
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      {hint && <div className="hint">{hint}</div>}
    </div>
  );
}

export function ManagementPage() {
  const [report, setReport] = useState<ManagementReport | null>(null);
  const [months, setMonths] = useState(6);
  const [error, setError] = useState('');

  useEffect(() => {
    setReport(null);
    api<ManagementReport>('/api/dashboard/management', { query: { months } })
      .then(setReport)
      .catch((err) => setError(errorText(err)));
  }, [months]);

  if (error) return <Notice kind="error">{error}</Notice>;
  if (!report) return <Loading />;

  const bookedTotal = report.revenue.reduce((sum, row) => sum + row.booked_cents, 0);
  const currentMonth = report.revenue[report.revenue.length - 1];
  const previousMonth = report.revenue[report.revenue.length - 2];
  const trend =
    previousMonth && previousMonth.booked_cents > 0
      ? Math.round(
          ((currentMonth!.booked_cents - previousMonth.booked_cents) / previousMonth.booked_cents) * 100,
        )
      : null;

  const unbilledTotal = report.unbilled.reduce((sum, row) => sum + row.amount_cents, 0);
  const utilisation =
    report.utilisation.locations_total > 0
      ? Math.round((report.utilisation.locations_used / report.utilisation.locations_total) * 100)
      : 0;

  const openTotal =
    report.openWork.inbound + report.openWork.outbound + report.openWork.returns + report.openWork.counts;

  return (
    <>
      <div className="toolbar">
        <label className="field">
          <span>Zeitraum</span>
          <select value={months} onChange={(e) => setMonths(Number(e.target.value))}>
            {[3, 6, 12, 24].map((value) => (
              <option key={value} value={value}>
                letzte {value} Monate
              </option>
            ))}
          </select>
        </label>
        <span className="spacer" />
      </div>

      {report.openWork.failedJobs > 0 && (
        <Notice kind="error">
          {report.openWork.failedJobs} Hintergrundauftrag/-aufträge stehen auf Fehler.
        </Notice>
      )}

      <div className="grid kpi" style={{ marginBottom: 16 }}>
        <Tile
          label={`Umsatz ${months} Monate`}
          value={formatMoney(bookedTotal)}
          hint="festgeschriebene Rechnungen, netto"
        />
        <Tile
          label="Laufender Monat"
          value={formatMoney(currentMonth?.booked_cents ?? 0)}
          hint={
            trend === null
              ? 'kein Vormonatsvergleich'
              : `${trend >= 0 ? '+' : ''}${trend} % gegenüber Vormonat`
          }
        />
        <Tile
          label="Noch nicht abgerechnet"
          value={formatMoney(unbilledTotal)}
          alert={unbilledTotal > 0}
          hint="offene Handling-Ereignisse"
        />
        <Tile
          label="Lagerauslastung"
          value={`${utilisation} %`}
          hint={`${report.utilisation.locations_used} von ${report.utilisation.locations_total} Plätzen`}
        />
        <Tile
          label="Bestandswert eigene Ware"
          value={formatMoney(report.utilisation.stock_value_cents)}
          hint={`${formatQty(report.utilisation.stock_units)} Einheiten gesamt`}
        />
        <Tile
          label="Liefertreue"
          value={report.serviceLevel.percent === null ? '—' : `${report.serviceLevel.percent} %`}
          alert={report.serviceLevel.percent !== null && report.serviceLevel.percent < 95}
          hint={
            report.serviceLevel.shipped === 0
              ? 'keine Sendung mit Wunschtermin'
              : `${report.serviceLevel.onTime} von ${report.serviceLevel.shipped} termingerecht`
          }
        />
        <Tile
          label="Offene Vorgänge"
          value={String(openTotal)}
          hint={`${report.openWork.pickTasks} offene Pickaufträge`}
        />
      </div>

      <div className="grid two">
        <Card title="Umsatzentwicklung">
          {bookedTotal === 0 && report.revenue.every((row) => row.draft_cents === 0) ? (
            <Empty>Noch keine Rechnungen im Zeitraum.</Empty>
          ) : (
            <>
              <Bars
                data={report.revenue}
                labelOf={(row: ManagementReport['revenue'][number]) => row.month.slice(5)}
                valuesOf={(row: ManagementReport['revenue'][number]) => [row.booked_cents, row.draft_cents]}
                colors={['var(--ok)', 'var(--warn)']}
              />
              <div className="legend">
                <span>
                  <i style={{ background: 'var(--ok)' }} />
                  festgeschrieben
                </span>
                <span>
                  <i style={{ background: 'var(--warn)' }} />
                  Entwurf
                </span>
              </div>
            </>
          )}
        </Card>

        <Card title="Warendurchsatz je Woche">
          <Bars
            data={report.throughput}
            labelOf={(row: ManagementReport['throughput'][number]) => row.week.slice(7)}
            valuesOf={(row: ManagementReport['throughput'][number]) => [row.eingang, row.ausgang]}
            colors={['var(--ok)', 'var(--accent)']}
          />
          <div className="legend">
            <span>
              <i style={{ background: 'var(--ok)' }} />
              Eingang
            </span>
            <span>
              <i style={{ background: 'var(--accent)' }} />
              Ausgang
            </span>
          </div>
        </Card>
      </div>

      <div className="grid two">
        <Card title="Umsatz je Auftraggeber" padded={false}>
          {report.revenueByOwner.length === 0 ? (
            <Empty>Noch keine abgerechneten Leistungen.</Empty>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Auftraggeber</th>
                  <th className="num">Rechnungen</th>
                  <th className="num">Umsatz</th>
                  <th className="num">Anteil</th>
                </tr>
              </thead>
              <tbody>
                {report.revenueByOwner.map((row) => (
                  <tr key={row.owner_id}>
                    <td>{row.owner_name}</td>
                    <td className="num">{row.invoices}</td>
                    <td className="num">{formatMoney(row.booked_cents)}</td>
                    <td className="num muted">
                      {bookedTotal > 0 ? `${Math.round((row.booked_cents / bookedTotal) * 100)} %` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title="Offene Abrechnung je Auftraggeber" padded={false}>
          {report.unbilled.length === 0 ? (
            <Empty>Alles abgerechnet.</Empty>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Auftraggeber</th>
                  <th className="num">Vorgänge</th>
                  <th className="num">Betrag</th>
                </tr>
              </thead>
              <tbody>
                {report.unbilled.map((row) => (
                  <tr key={row.owner_name}>
                    <td>{row.owner_name}</td>
                    <td className="num">{row.events}</td>
                    <td className="num">{formatMoney(row.amount_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      <div className="grid two">
        <Card title="Pickleistung (30 Tage)" padded={false}>
          {report.pickPerformance.length === 0 ? (
            <Empty>Im Zeitraum wurde nicht kommissioniert.</Empty>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Mitarbeiter</th>
                  <th className="num">Picks</th>
                  <th className="num">Einheiten</th>
                </tr>
              </thead>
              <tbody>
                {report.pickPerformance.map((row) => (
                  <tr key={row.user_name}>
                    <td>{row.user_name}</td>
                    <td className="num">{row.picks}</td>
                    <td className="num">{formatQty(row.units)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title="Ladenhüter" padded={false}>
          <div className="body small muted" style={{ paddingBottom: 0 }}>
            Liegt im Lager, hatte aber seit 90 Tagen keinen Warenausgang.
          </div>
          {report.slowMovers.length === 0 ? (
            <Empty>Keine Ladenhüter.</Empty>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Artikel</th>
                  <th>Eigentümer</th>
                  <th className="num">Bestand</th>
                  <th>Letzter Ausgang</th>
                </tr>
              </thead>
              <tbody>
                {report.slowMovers.map((row) => (
                  <tr key={row.sku + row.owner_name}>
                    <td>
                      <span className="mono">{row.sku}</span> {row.article_name}
                    </td>
                    <td className="small">{row.owner_name}</td>
                    <td className="num">{formatQty(row.qty)}</td>
                    <td className="small muted">
                      {row.last_movement ? formatDate(row.last_movement) : 'nie'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title="Negativbestände" padded={false}>
          <div className="body small muted" style={{ paddingBottom: 0 }}>
            Plätze, die rechnerisch ins Minus gelaufen sind — fast immer eine
            Inventurdifferenz. Ein Kassenverkauf ohne gebuchten Bestand landet
            hier, statt abgewiesen zu werden.
          </div>
          {report.negativeStock.length === 0 ? (
            <Empty>Kein Negativbestand — alles im Plus.</Empty>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Artikel</th>
                  <th>Eigentümer</th>
                  <th>Platz</th>
                  <th className="num">Bestand</th>
                  <th>Letzte Bewegung</th>
                </tr>
              </thead>
              <tbody>
                {report.negativeStock.map((row) => (
                  <tr key={row.sku + row.location_code}>
                    <td>
                      <span className="mono">{row.sku}</span> {row.article_name}
                    </td>
                    <td className="small">{row.owner_name}</td>
                    <td className="mono small">{row.location_code}</td>
                    <td className="num" style={{ color: 'var(--bad)', fontWeight: 600 }}>
                      {formatQty(row.qty)}
                    </td>
                    <td className="small muted">
                      {row.last_movement ? formatDate(row.last_movement) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </>
  );
}
